import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { Input, Select, snackbar } from "@mobiscroll/react5";
import Loader from "src/components/Dashboard/common/Loader";
import fileToBase64 from "src/lib/fileToBase64";
import { getClinicProfile } from "src/components/Admin/Subscription/api";
import { addClinicBankDetails, updateClinic } from "./api";

const EditClinicModal = ({ show, clinicId, onClose }) => {
    const [formData, setFormData] = useState({ name: "", address: "", helplineNumber: "", logoLink: "", freeUntil: "" });
    const [originalData, setOriginalData] = useState({ name: "", address: "", helplineNumber: "", logoLink: "", freeUntil: "" });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Logo upload states
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Bank details state — only submitted if user touches at least one field
    const bankEmptyState = { bankAccountNumber: "", ifscCode: "", accountType: "savings", businessType: "proprietorship", beneficiaryName: "" };
    const [bankData, setBankData] = useState(bankEmptyState);
    const [bankTouched, setBankTouched] = useState(false);
    const [bankErrors, setBankErrors] = useState({});

    const [addBank, setAddBank] = useState(false);

    useEffect(() => {
        if (!show || !clinicId) {
            setFormData({ name: "", address: "", helplineNumber: "", logoLink: "", freeUntil: "" });
            setOriginalData({ name: "", address: "", helplineNumber: "", logoLink: "", freeUntil: "" });
            setLogoFile(null);
            setLogoPreview(null);
            setError(null);
            setBankData(bankEmptyState);
            setBankTouched(false);
            setBankErrors({});
            return;
        }

        const fetchClinicProfile = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getClinicProfile(clinicId);
                const clinicData = {
                    name: data?.name || "",
                    address: data?.address || "",
                    helplineNumber: data?.helplineNumber || "",
                    logoLink: data?.logoLink || "",
                    freeUntil: data?.freeUntil || ""
                };
                setFormData(clinicData);
                setOriginalData(clinicData);
            } catch (err) {
                setError(err.message || "Failed to load clinic details");
            } finally {
                setLoading(false);
            }
        };

        fetchClinicProfile();
    }, [show, clinicId]);

    const handleInputChange = (fieldName, e) => {
        const val = (e && e.target) ? e.target.value : e.value;
        setFormData(prev => ({ ...prev, [fieldName]: val }));
    };

    const handleClose = () => {
        setFormData(originalData);
        setLogoFile(null);
        setLogoPreview(null);
        setError(null);
        setBankData(bankEmptyState);
        setBankTouched(false);
        setBankErrors({});
        onClose();
    };

    const handleLogoFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
        if (!validImageTypes.includes(file.type)) {
            snackbar({ message: "Only image files are allowed (JPEG, PNG, GIF, WebP)", color: "danger" });
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            snackbar({ message: "File size must be less than 5MB", color: "danger" });
            return;
        }

        setLogoFile(file);

        try {
            const preview = await fileToBase64(file);
            setLogoPreview(preview);
        } catch (err) {
            console.error("Error processing image:", err);
            snackbar({ message: "Error processing image", color: "danger" });
            setLogoFile(null);
            setLogoPreview(null);
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const convertLogoToBase64 = async (fileToUpload) => {
        setLogoUploading(true);
        try {
            const base64 = await fileToBase64(fileToUpload);
            // Strip the "data:image/...;base64," prefix — server expects raw base64
            return base64.split(",")[1];
        } catch (error) {
            console.error("Error converting logo to base64:", error);
            throw error;
        } finally {
            setLogoUploading(false);
        }
    };

    const handleBankChange = (field, val) => {
        setBankData(prev => ({ ...prev, [field]: val }));
        setBankTouched(true);
        setBankErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const validateBankData = () => {
        const errs = {};
        if (!bankData.bankAccountNumber.trim()) {
            errs.bankAccountNumber = "Account number is required";
        } else if (!/^\d+$/.test(bankData.bankAccountNumber.trim())) {
            errs.bankAccountNumber = "Digits only";
        }
        if (!bankData.ifscCode.trim()) {
            errs.ifscCode = "IFSC code is required";
        } else if (bankData.ifscCode.trim().length !== 11) {
            errs.ifscCode = "Must be exactly 11 characters";
        }
        setBankErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        // If user touched bank fields, validate before proceeding
        if (bankTouched && !validateBankData()) return;

        setSubmitting(true);
        try {
            // ── Clinic details update ──────────────────────────────────────────
            let logoBase64 = null;
            if (logoFile) {
                snackbar({ message: "Processing logo...", color: "info" });
                logoBase64 = await convertLogoToBase64(logoFile);
            }

            const updates = Object.keys(formData).reduce((acc, key) => {
                if (formData[key] !== originalData[key]) acc[key] = formData[key];
                return acc;
            }, {});

            if (logoBase64) updates.logoLink = logoBase64;

            if (Object.keys(updates).length > 0) {
                const result = await updateClinic({ clinicId, ...updates });
                if (result.success) {
                    setLogoFile(null);
                    setLogoPreview(null);
                }
            }

            // ── Bank details — only if user entered something ──────────────────
            if (bankTouched) {
                await addClinicBankDetails({
                    clinicId,
                    bankAccountNumber: bankData.bankAccountNumber.trim(),
                    ifscCode: bankData.ifscCode.trim(),
                    accountType: bankData.accountType,
                    businessType: bankData.businessType,
                    beneficiaryName: bankData.beneficiaryName.trim() || undefined,
                });
            }

            if (Object.keys(updates).length === 0 && !bankTouched) {
                snackbar({ message: "No changes to submit", color: "warning" });
                return;
            }

            setTimeout(() => onClose(), 500);
        } catch (err) {
            console.error("Submission failed:", err);
            snackbar({ message: "Update failed. Please try again.", color: "danger" });
        } finally {
            setSubmitting(false);
        }
    };

    const hasChanges = () => {
        return (
            bankTouched ||
            logoFile !== null ||
            formData.name !== originalData.name ||
            formData.address !== originalData.address ||
            formData.helplineNumber !== originalData.helplineNumber ||
            formData.freeUntil !== originalData.freeUntil
        );
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            dialogClassName="docg-modal"
            size="xl"
            centered
            // style={{ minHeight: "70vh" }}
            scrollable
        >
            <Loader show={loading || submitting || logoUploading} />
            <Modal.Header closeButton style={{ background: '#DEE2E6', color: '#6C5B7B' }}>
                <Modal.Title>Update Clinic Details</Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                {error ? (
                    <div className="alert alert-danger">
                        <p>{error}</p>
                        <button onClick={handleClose} className="docg-save-btn">Close</button>
                    </div>
                ) : (
                    <>
                        {/* Clinic Details  */}
                        <div className="textarea-wrap py-4 mb-4">
                            <span className="purple-bold">Clinic Details</span>
                            <div className="mbsc-grid">
                                <div className="mbsc-row">
                                    <div className="mbsc-col-4">
                                        <Input
                                            inputStyle="underline"
                                            labelStyle="floating"
                                            label="Clinic Name (not editable)"
                                            placeholder="Clinic name"
                                            value={formData.name}
                                            type="text"
                                            disabled
                                        />
                                    </div>
                                    <div className="mbsc-col-4">
                                        <Input
                                            inputStyle="underline"
                                            labelStyle="floating"
                                            label="Helpline Number"
                                            placeholder="Enter helpline number"
                                            value={formData.helplineNumber}
                                            onChange={(e) => handleInputChange("helplineNumber", e)}
                                            type="text"
                                        />
                                    </div>
                                    <div className="mbsc-col-4">
                                        <Input
                                            inputStyle="underline"
                                            labelStyle="floating"
                                            label="Free Trial Period (in Days)"
                                            placeholder="Enter number of days"
                                            value={formData.freeUntil}
                                            onChange={(e) => handleInputChange("freeUntil", e)}
                                            type="text"
                                        />
                                    </div>
                                </div>

                                <div className="mbsc-row mt-4 align-items-end">
                                    <div className="mbsc-col-6">
                                        <Input
                                            inputStyle="underline"
                                            labelStyle="floating"
                                            label="Clinic Address"
                                            placeholder="Enter clinic address"
                                            value={formData.address}
                                            onChange={(e) => handleInputChange("address", e)}
                                            type="text"
                                        />
                                    </div>

                                    {/* Completely Inline Logo Section */}
                                    <div className="mbsc-col-6">
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "15px",
                                            borderBottom: "1px solid #E0E0E0",
                                            paddingBottom: "8px",
                                            minHeight: "54px"
                                        }}>
                                            {/* Preview Wrapper */}
                                            <div style={{ flexShrink: 0 }}>
                                                {logoPreview ? (
                                                    <img
                                                        src={logoPreview}
                                                        alt="New Preview"
                                                        style={{ height: "50px", width: "50px", objectFit: "cover", borderRadius: "4px", border: "1px solid #6C5B7B" }}
                                                    />
                                                ) : formData.logoLink ? (
                                                    <img
                                                        src={formData.logoLink}
                                                        alt="Current Logo"
                                                        style={{ height: "50px", width: "50px", objectFit: "cover", borderRadius: "4px", border: "1px solid #ccc" }}
                                                    />
                                                ) : (
                                                    <div style={{ height: "50px", width: "50px", borderRadius: "4px", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "#888", border: "1px dashed #ccc" }}>
                                                        None
                                                    </div>
                                                )}
                                            </div>

                                            {/* Labels and Button */}
                                            <div style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                                                <span style={{ fontSize: "12px", color: "#888", marginBottom: "2px" }}>Clinic Logo</span>
                                                <span style={{ fontSize: "13px", fontWeight: "500", color: logoFile ? "#333" : "#999", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {logoFile ? logoFile.name : "No file chosen"}
                                                </span>
                                            </div>

                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleLogoFileSelect}
                                                style={{ display: "none" }}
                                            />

                                            <button
                                                className="docg-outline-btn"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={submitting}
                                                type="button"
                                            // style={{ padding: "5px 12px", fontSize: "13px", whiteSpace: "nowrap" }}
                                            >
                                                {logoPreview ? "Change" : "Browse"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button className="docg-outline-btn my-2" onClick={() => setAddBank(!addBank)}>
                            {addBank ? "Hide Bank Details \u25B2" : "Add Bank Details \u25BC"}
                        </button>

                        {/* Bank Details */}
                        {addBank && (
                            <div className="textarea-wrap" style={{ marginTop: "30px", paddingBottom: "40px" }}>
                                <span className="purple-bold">Bank Details</span>
                                <div className="mbsc-grid">
                                    <div className="mbsc-row">
                                        <div className="mbsc-col-4">
                                            <Input
                                                inputStyle="underline"
                                                labelStyle="floating"
                                                label="Account Number*"
                                                placeholder="Enter account number"
                                                value={bankData.bankAccountNumber}
                                                onChange={(e) => handleBankChange("bankAccountNumber", e.target.value)}
                                                type="text"
                                                error={!!bankErrors.bankAccountNumber}
                                                errorMessage={bankErrors.bankAccountNumber}
                                            />
                                        </div>
                                        <div className="mbsc-col-4">
                                            <Input
                                                inputStyle="underline"
                                                labelStyle="floating"
                                                label="IFSC Code*"
                                                placeholder="Enter 11-character IFSC code"
                                                value={bankData.ifscCode}
                                                onChange={(e) => handleBankChange("ifscCode", e.target.value)}
                                                type="text"
                                                error={!!bankErrors.ifscCode}
                                                errorMessage={bankErrors.ifscCode}
                                            />
                                        </div>
                                        <div className="mbsc-col-4">
                                            <Input
                                                inputStyle="underline"
                                                labelStyle="floating"
                                                label="Beneficiary Name (Optional)"
                                                placeholder="Enter beneficiary name"
                                                value={bankData.beneficiaryName}
                                                onChange={(e) => handleBankChange("beneficiaryName", e.target.value)}
                                                type="text"
                                            />
                                        </div>
                                    </div>
                                    <div className="mbsc-row mt-4">
                                        <div className="mbsc-col-6">
                                            <Select
                                                label="Account Type*"
                                                labelStyle="floating"
                                                inputStyle="underline"
                                                data={[
                                                    { text: "Savings", value: "savings" },
                                                    { text: "Current", value: "current" }
                                                ]}
                                                value={bankData.accountType}
                                                onChange={(ev) => handleBankChange("accountType", ev.value)}
                                                themeVariant="light"
                                            />
                                        </div>
                                        <div className="mbsc-col-6">
                                            <Select
                                                label="Business Type*"
                                                labelStyle="floating"
                                                inputStyle="underline"
                                                data={[
                                                    { text: "Proprietorship", value: "proprietorship" },
                                                    { text: "Partnership", value: "partnership" },
                                                    { text: "Private Limited", value: "private_limited" },
                                                    { text: "Public Limited", value: "public_limited" },
                                                    { text: "LLP", value: "llp" },
                                                    { text: "NGO", value: "ngo" },
                                                    { text: "Trust", value: "trust" },
                                                    { text: "Society", value: "society" },
                                                    { text: "HUF", value: "huf" },
                                                    { text: "Not Yet Registered", value: "not_yet_registered" }
                                                ]}
                                                value={bankData.businessType}
                                                onChange={(ev) => handleBankChange("businessType", ev.value)}
                                                themeVariant="light"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="justify-content-between p-4">
                <button onClick={handleClose} className="docg-patient-cancel-btn" disabled={submitting}>
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    className="docg-save-btn"
                    disabled={!hasChanges() || submitting}
                >
                    {submitting ? "Submitting..." : "Submit Details"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditClinicModal;